import React, { useState, useEffect, useRef } from 'react';

/**
 * Searchable Property Selector with Recent Properties
 */
export default function PropertySelector({ properties, selectedProperty, onPropertyChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentProperties, setRecentProperties] = useState([]);
  const dropdownRef = useRef(null);

  // Load recent properties from localStorage
  useEffect(() => {
    const recent = JSON.parse(localStorage.getItem('recentProperties') || '[]');
    setRecentProperties(recent);
  }, []);

  // Save to recent when property changes
  useEffect(() => {
    if (selectedProperty) {
      const recent = JSON.parse(localStorage.getItem('recentProperties') || '[]');
      const updated = [
        selectedProperty,
        ...recent.filter(id => id !== selectedProperty)
      ].slice(0, 5); // Keep last 5

      localStorage.setItem('recentProperties', JSON.stringify(updated));
      setRecentProperties(updated);
    }
  }, [selectedProperty]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter properties based on search
  const filteredProperties = properties.filter(prop => {
    const searchLower = searchQuery.toLowerCase();
    return (
      prop.displayName?.toLowerCase().includes(searchLower) ||
      prop.name?.toLowerCase().includes(searchLower) ||
      prop.id?.toLowerCase().includes(searchLower)
    );
  });

  // Get recent property objects
  const recentPropertyObjects = recentProperties
    .map(id => properties.find(p => p.id === id))
    .filter(Boolean)
    .slice(0, 3); // Show top 3

  // Get selected property object
  const selectedProp = properties.find(p => p.id === selectedProperty);

  const handleSelect = (propertyId) => {
    onPropertyChange(propertyId);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Auto-open when typing
  useEffect(() => {
    if (searchQuery) {
      setIsOpen(true);
    }
  }, [searchQuery]);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Search Input - Always Visible */}
      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-muted-light dark:text-muted-dark">
          search
        </span>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsOpen(true)}
          placeholder={selectedProp?.displayName || selectedProp?.name || 'Hledat GA4 property...'}
          className="w-full pl-10 pr-10 py-2 bg-background-light dark:bg-background-dark border border-border-light dark:border-border-dark rounded-lg text-sm text-text-light dark:text-text-dark placeholder-muted-light dark:placeholder-muted-dark focus:outline-none focus:border-primary"
        />
        {selectedProp && !searchQuery && (
          <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-primary">
            check_circle
          </span>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-surface-light dark:bg-surface-dark border border-border-light dark:border-border-dark rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
            {/* Recent Properties */}
            {!searchQuery && recentPropertyObjects.length > 0 && (
              <div className="py-2">
                <div className="px-3 py-1 text-xs font-semibold text-muted-light dark:text-muted-dark uppercase">
                  Nedávno použité
                </div>
                {recentPropertyObjects.map((prop) => (
                  <button
                    key={prop.id}
                    onClick={() => handleSelect(prop.id)}
                    className={`w-full px-3 py-2 text-left hover:bg-primary-light dark:hover:bg-primary-dark transition-colors flex items-center gap-2 ${
                      selectedProperty === prop.id ? 'bg-primary-light dark:bg-primary-dark' : ''
                    }`}
                  >
                    <span className="material-symbols-outlined text-sm text-primary">
                      history
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                        {prop.displayName || prop.name}
                      </div>
                      <div className="text-xs text-muted-light dark:text-muted-dark truncate">
                        {prop.id}
                      </div>
                    </div>
                    {selectedProperty === prop.id && (
                      <span className="material-symbols-outlined text-primary">
                        check
                      </span>
                    )}
                  </button>
                ))}
                <div className="h-px bg-border-light dark:bg-border-dark my-2" />
              </div>
            )}

            {/* All Properties */}
            {filteredProperties.length > 0 ? (
              <div className="py-2">
                <div className="px-3 py-1 text-xs font-semibold text-muted-light dark:text-muted-dark uppercase">
                  {searchQuery ? `Nalezeno ${filteredProperties.length}` : `Všechny property (${properties.length})`}
                </div>
                {filteredProperties.map((prop) => (
                  <button
                    key={prop.id}
                    onClick={() => handleSelect(prop.id)}
                    className={`w-full px-3 py-2 text-left hover:bg-primary-light dark:hover:bg-primary-dark transition-colors ${
                      selectedProperty === prop.id ? 'bg-primary-light dark:bg-primary-dark' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-text-light dark:text-text-dark truncate">
                          {prop.displayName || prop.name}
                        </div>
                        <div className="text-xs text-muted-light dark:text-muted-dark truncate">
                          {prop.id}
                        </div>
                      </div>
                      {selectedProperty === prop.id && (
                        <span className="material-symbols-outlined text-primary">
                          check
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-muted-light dark:text-muted-dark">
                Žádné výsledky pro "{searchQuery}"
              </div>
            )}
        </div>
      )}
    </div>
  );
}
